import { computed, inject, Injectable, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../core/services/language.service';
import {
	Icons,
	IFilter,
	IMenu,
	ISelect,
	IStepProgress,
	ITab,
	ITableCell,
	ITableConfig,
	ITableHeader,
	ITableRow,
	ITableSortEvent,
	ITimelineItem,
	IToolbar,
} from '@tecalisdev/infinity';
import { MasterService } from '../../shared/services/master.service';
import { formatDate, truncateUUID, notAvailableLabel } from '../../core/utils/utils';
import { createDateRangeFilters } from '../../shared/list/create-date-range-filters.util';
import { buildCrudStatusMenu } from '../../shared/utils/build-crud-status-menu.util';
import {
	CONFIDENCE_THRESHOLD,
	DEMAND_LEVEL,
	IDocInfo,
	IOperationInfo,
	IPolDemandLevel,
	IWorkflowsListBody,
	TAB_WORKFLOW_DETAIL,
	TAB_WORKFLOW_DOC_DETAILS,
	TAB_WORKFLOW_OCR_FRAUD,
	WorkflowStep,
} from '../models/workflows.models';
import { FormGroup } from '@angular/forms';
import { MenuItemAction, RowItemAction } from '../../shared/models/master.models';
import {
	CONNECTION_TYPE,
	DOCS_TYPE,
	IPolSetting,
	OPERATION_TYPE,
	POL_TEST,
	POL_TYPE,
} from '../../operations/models/operations.models';
import { RolePermissionsService } from '../../core/services/role-permissions.service';
import { Permission } from '../../core/models/role-permissions.models';
import { PDFS } from '../../core/constants/pdf.constants';
import { WorkflowFormBuilderService } from './workflow-form-builder.service';
import {
	getPolScoringLabel,
	sanitizePolAvailableTests as sanitizePolAvailableTestsUtil,
	sanitizePolSetting as sanitizePolSettingUtil,
	sanitizePolTotalTest as sanitizePolTotalTestUtil,
	selectPolDemandLevel as selectPolDemandLevelUtil,
} from '../utils/workflow-pol.util';

/**
 * Fachada del dominio workflows (NIF-530): listado, menús, tabs y helpers de UI.
 * Delega schema FormGroup a `WorkflowFormBuilderService` y sanitize POL a `workflow-pol.util`.
 * Mapa: `../WORKFLOWS_EDITOR.md`.
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowsHelperService {
	public trans = inject(TranslateService);
	public masterService = inject(MasterService);
	private langSvc = inject(LanguageService);
	private rolePermissions = inject(RolePermissionsService);
	private formBuilder = inject(WorkflowFormBuilderService);

	/** FormGroup con un control por locale permitido (mismo texto inicial opcional en todos). */
	public createLocaleTextFormGroup(initialSingle?: string, requireAllLocales = false): FormGroup {
		return this.formBuilder.createLocaleTextFormGroup(initialSingle, requireAllLocales);
	}

	/** Un valor inicial por idioma (p. ej. copys distintos en es y en). */
	public createLocaleTextFormGroupWithDefaults(
		defaultsByLocale: Readonly<Record<string, string>>,
		requireAllLocales = false,
	): FormGroup {
		return this.formBuilder.createLocaleTextFormGroupWithDefaults(defaultsByLocale, requireAllLocales);
	}

	/** Activa o quita obligatoriedad en todos los locales de un grupo (p. ej. si la operación no está disponible). */
	public setLocaleTextGroupRequired(group: FormGroup | null | undefined, required: boolean): void {
		this.formBuilder.setLocaleTextGroupRequired(group, required);
	}

	/** Título de documento: obligatorio en todos los idiomas permitidos. */
	public createDocTitleLocaleFormGroup(): FormGroup {
		return this.formBuilder.createDocTitleLocaleFormGroup();
	}

	/**
	 * Pauta del paso FORM (Figma «Pauta*»): obligatoria en todos los idiomas permitidos.
	 */
	public applyFormStepGuidelineValidatorsForPreset(
		guidelineGroup: FormGroup | null | undefined,
		_preset?: { fieldMetadataLocked?: boolean; prefillGuideline?: boolean } | null,
	): void {
		this.formBuilder.applyFormStepGuidelineValidatorsForPreset(guidelineGroup, _preset);
	}

	/** Descripción del paso (Figma sin asterisco): opcional; si hay texto en algún idioma, todos obligatorios. */
	public applyFormStepDescriptionValidators(descriptionGroup: FormGroup | null | undefined): void {
		this.formBuilder.applyFormStepDescriptionValidators(descriptionGroup);
	}

	/**
	 * Ajusta validación de pautas según tipo de documento en el modal:
	 * - GENÉRICO: obligatorio en todos los idiomas permitidos.
	 * - Resto: opcional si todo vacío; si hay texto en algún idioma, obligatorio completar todos.
	 */
	public applyDocInstructionsValidatorsForType(
		instructionsGroup: FormGroup | null | undefined,
		docType: string,
	): void {
		this.formBuilder.applyDocInstructionsValidatorsForType(instructionsGroup, docType);
	}

	public readonly toolbar: Signal<IToolbar> = computed<IToolbar>(() => {
		this.langSvc.currentLang(); // dependencia reactiva: recalcula al cambiar idioma
		const filters: IFilter[] = [
			{
				type: 'select',
				title: this.trans.instant('FILTERS.STATE'),
				name: 'status',
				placeholder: this.trans.instant('FILTERS.STATE'),
				options: [
					{ id: 'active', label: this.trans.instant('MASTER.STATUS.ACTIVE') },
					{ id: 'inactive', label: this.trans.instant('MASTER.STATUS.INACTIVE') },
					{ id: 'draft', label: this.trans.instant('MASTER.STATUS.DRAFT') },
				],
			},
			...createDateRangeFilters({
				fromTitle: this.trans.instant('FILTERS.FROM'),
				toTitle: this.trans.instant('FILTERS.TO'),
			}),
		];

		// Si hay mÃƒÂ¡s de una compaÃƒÂ±ÃƒÂ­a, agregar filtro de compaÃƒÂ±ÃƒÂ­a
		if (this.masterService.companies.length > 1 || this.rolePermissions.isSuperadmin()) {
			// Poner en la posiciÃƒÂ³n 2 el filtro de compaÃƒÂ±ÃƒÂ­a
			filters.splice(1, 0, {
				type: 'select',
				title: this.trans.instant('FILTERS.COMPANY'),
				name: 'company_uuid',
				placeholder: this.trans.instant('FILTERS.COMPANY'),
				options: this.masterService.companies,
				search: true,
			});
		}

		return {
			filters,
			search: {
				placeholder: this.trans.instant('FILTERS.SEARCH'),
				name: 'search',
			},
			...(this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)
				? {
						action: {
							id: MenuItemAction.SHOW_CURL,
							icon: 'icon-code-brackets',
							tooltip: this.trans.instant('WORKFLOWS.LIST.CURL_LIST_TOOLTIP'),
						},
					}
				: {}),
		};
	});

	public get header(): ITableHeader[] {
		const showCompany = this.masterService.companies.length > 1;
		const headers: ITableHeader[] = [
			{ label: this.trans.instant('TABLE.HEADER.NAME'), sortable: true, sortKey: 'alias' },
			...(showCompany
				? [
						{
							label: this.trans.instant('TABLE.HEADER.COMPANY'),
							sortable: true,
							sortKey: 'company',
						} satisfies ITableHeader,
					]
				: []),
			{ label: this.trans.instant('TABLE.HEADER.ID'), sortable: true, sortKey: 'uuid' },
			{ label: this.trans.instant('TABLE.HEADER.OPERATIONS') },
			{ label: this.trans.instant('TABLE.HEADER.STATE') },
			{
				label: this.trans.instant('TABLE.HEADER.DATE_CREATE'),
				sortable: true,
				sortKey: 'created_at',
				align: 'right',
			},
		];

		// Si es superadmin, mostrar columna de apikey (antes de operaciones)
		if (this.rolePermissions.isSuperadmin()) {
			const operationsIndex = headers.findIndex(
				(h) => h.label === this.trans.instant('TABLE.HEADER.OPERATIONS'),
			);
			headers.splice(operationsIndex, 0, { label: this.trans.instant('TABLE.HEADER.APIKEY') });
		}

		// Una sola columna de acciones de fila si hay al menos una acción disponible
		const hasRowActions =
			this.rolePermissions.canViewWorkflowJson() ||
			this.rolePermissions.canLaunchWorkflowDemo() !== false;
		if (hasRowActions) {
			headers.push({ label: '' });
		}

		return headers;
	}

	public get config(): ITableConfig {
		return this.getConfig(true);
	}

	public get sort(): ITableSortEvent {
		return { ...this.resetSort() };
	}

	public resetSort(): ITableSortEvent {
		return {
			sortKey: 'created_at',
			sortDirection: 'desc',
			headerIndex: this.header.findIndex((h) => h.sortKey === 'created_at') || 0,
		};
	}

	/**
	 * Obtiene la configuraciÃƒÂ³n de la tabla con opciÃƒÂ³n de clickeable basada en permisos
	 */
	public getConfig(isClickeable: boolean = false): ITableConfig {
		return {
			clickeable: isClickeable,
			pagination: {
				page: 1,
				limit: 25,
				total: 0,
				label: this.trans.instant('TABLE.ELEMENTS_BY_PAGE'),
				elementsByPage: [
					{ id: '25', label: '25' },
					{ id: '50', label: '50' },
					{ id: '100', label: '100' },
				],
			},
		};
	}

	public transformRow(
		response: IWorkflowsListBody[],
		permissions?: { canEdit: boolean; canDelete: boolean; canDemo: boolean; canDuplicateToEnv: boolean },
	): ITableRow[] {
		return response.map((row) => this.transformListTableRow(row, permissions));
	}

	public transformListTableRow(
		row: IWorkflowsListBody,
		permissions?: { canDemo: boolean; canDuplicateToEnv?: boolean },
	): ITableRow {
		const timeline: ITimelineItem[] = this.timelineWorkflowOperations(
			this.normalizeListOperations(row.operations),
		);
		const showCompany = this.masterService.companies.length > 1;
		const cells: ITableCell[] = [
			{ label: row.alias },
			...(showCompany
				? [
						{
							label: this.masterService.getNameCompany(row.company_uuid) || notAvailableLabel(this.trans),
						} satisfies ITableCell,
					]
				: []),
			{ label: truncateUUID(row.uuid), copyText: { labelToCopy: row.uuid } },
			{
				label: timeline.length > 0 ? '' : notAvailableLabel(this.trans),
				timeline: timeline.length > 0 ? timeline : undefined,
			},
			{
				label: '',
				tag:
					row.status === 'active'
						? { label: this.trans.instant(`MASTER.STATUS.ACTIVE`), status: 'success' }
						: { label: this.trans.instant(`MASTER.STATUS.INACTIVE`), status: 'disabled' },
			},
			{ label: formatDate(row.created_at), align: 'right' },
		];

		// Si es superadmin, mostrar columna de apikey (después del ID, antes de operaciones)
		if (this.rolePermissions.isSuperadmin()) {
			const uuidIndex = cells.findIndex((c) => !!c.copyText);
			cells.splice(uuidIndex >= 0 ? uuidIndex + 1 : 2, 0, { label: row.api_key_alias });
		}

		const actionCell = this.buildListRowActionCell(permissions);
		if (actionCell) {
			cells.push(actionCell);
		}

		return {
			clickValue: row,
			cells,
		};
	}

	/**
	 * Acciones de fila del listado: menú si hay ≥2; button-icon si solo 1 (NIF-782).
	 * Orden menú Figma: demo → duplicar a otro entorno.
	 */
	private buildListRowActionCell(
		permissions?: { canDemo: boolean; canDuplicateToEnv?: boolean },
	): ITableCell | null {
		const menu: IMenu[] = [];

		if (permissions?.canDemo !== false) {
			menu.push({
				id: RowItemAction.DEMO,
				icon: 'icon-play',
				label: this.trans.instant('WORKFLOWS.MENU.LAUNCH_DEMO'),
			});
		}
		if (permissions?.canDuplicateToEnv) {
			menu.push({
				id: RowItemAction.DUPLICATE,
				icon: 'icon-copy',
				label: this.trans.instant('WORKFLOWS.DUPLICATE_TO_ENV.TOOLTIP'),
			});
		}

		if (menu.length === 0) {
			return null;
		}

		if (menu.length === 1) {
			const only = menu[0];
			return {
				label: '',
				tooltip: only.label,
				buttonIcon: {
					id: String(only.id),
					icon: only.icon as Icons,
				},
				align: 'right',
			};
		}

		return {
			label: '',
			align: 'right',
			buttonIcon: {
				id: 'workflow-actions',
				icon: 'icon-more',
				menu,
			},
		};
	}

	/** Tipos del listado en mayÃƒÂºsculas; ignora vacíos. Contrato API: siempre `string[]`. */
	private normalizeListOperations(ops?: string[]): string[] {
		if (!ops?.length) {
			return [];
		}
		return ops.filter((op) => typeof op === 'string' && op.trim() !== '').map((op) => op.toUpperCase());
	}

	/** Timeline de iconos de operaciones del workflow (sin estado de ejecuciÃƒÂ³n). */
	private timelineWorkflowOperations(types: string[]): ITimelineItem[] {
		return types
			.filter((type) => type !== OPERATION_TYPE.HOME && type !== OPERATION_TYPE.FINISH)
			.map((type) => {
				const title = this.getListOperationTitle(type);
				return {
					icon: this.getListOperationIcon(type),
					status: 'default',
					title,
					extraInfo: {
						infoTitle: title,
					},
				};
			});
	}

	private getListOperationTitle(type: string): string {
		switch (type) {
			case OPERATION_TYPE.TFA:
				return this.trans.instant('PROCESSES.DETAIL.TFA');
			case OPERATION_TYPE.FORM:
				return this.trans.instant('OPERATIONS.FORM');
			default:
				return this.trans.instant(`WORKFLOWS.FORM.OPERATIONS.${type}`);
		}
	}

	private getListOperationIcon(type: string): Icons {
		switch (type) {
			case OPERATION_TYPE.OCR:
				return 'icon-card';
			case OPERATION_TYPE.POL:
				return 'icon-user';
			case OPERATION_TYPE.DOCUMENTS:
				return 'icon-file';
			case OPERATION_TYPE.CONNECTIONS:
			case OPERATION_TYPE.EVALUATION:
			case OPERATION_TYPE.FINANCIAL_EVALUATION:
				return 'icon-euro';
			case OPERATION_TYPE.ATTORNEY_POWERS:
				return 'icon-shield-check';
			case OPERATION_TYPE.TFA:
				return 'icon-smartphone';
			case OPERATION_TYPE.FORM:
				return 'icon-fill-form';
			case OPERATION_TYPE.HOME:
				return 'icon-home';
			case OPERATION_TYPE.FINISH:
				return 'icon-external-link';
			default:
				return 'icon-card';
		}
	}

	// FORM
	public changeStep(step: IStepProgress, steps: Array<IStepProgress>, form: FormGroup, showError: string): void {
		const selectedIndex = steps.findIndex((sp: IStepProgress) => sp.selected);
		const clickedIndex = steps.findIndex((sp: IStepProgress) => sp.id === step.id);
		if (selectedIndex === clickedIndex) {
			return;
		}
		if (selectedIndex > clickedIndex) {
			if (form.disabled) {
				return;
			}
			steps.map((sp: IStepProgress) => (sp.selected = false));
			step.selected = true;
			showError = '';
			return;
		}
		if (selectedIndex < clickedIndex) {
			if (form.get(steps[selectedIndex].id).valid) {
				steps.map((sp: IStepProgress, index: number) => {
					sp.selected = index <= clickedIndex ? !form.get(sp.id).valid : false;
				});
				if (steps.every((sp: IStepProgress) => !sp.selected)) {
					steps[clickedIndex].selected = true;
				}
			} else {
				form.get(steps[selectedIndex].id).markAllAsTouched();
				const element = document.getElementsByClassName('template-create')[0];
				const firstInvalidControl = element.getElementsByClassName('ng-invalid')[0];
				if (firstInvalidControl) {
					firstInvalidControl?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
					(firstInvalidControl as HTMLElement).focus();
				}
			}
			showError = '';
			return;
		}
	}

	public get steps(): IStepProgress[] {
		return [
			{ label: this.trans.instant('WORKFLOWS.STEPS.GENERAL'), selected: true, id: WorkflowStep.GENERAL },
			{ label: this.trans.instant('WORKFLOWS.STEPS.OPERATIONS'), selected: false, id: WorkflowStep.OPERATIONS },
			{
				label: this.trans.instant('WORKFLOWS.STEPS.CONFIRMATION'),
				selected: false,
				id: WorkflowStep.CONFIRMATION,
			},
		];
	}

	public get policyChecksMenuOpt(): IMenu[] {
		return [
			{
				label: this.trans.instant('BUTTON.REMOVE'),
				icon: 'icon-x-circle',
				id: MenuItemAction.DELETE,
				style: 'red',
			},
		];
	}

	public get documentsMenuOpt(): IMenu[] {
		return [
			{
				label: this.trans.instant('BUTTON.EDIT'),
				icon: 'icon-eye',
				id: MenuItemAction.EDIT,
			},
			{
				label: this.trans.instant('BUTTON.REMOVE'),
				icon: 'icon-x-circle',
				id: MenuItemAction.DELETE,
				divider: true,
				style: 'red',
			},
		];
	}

	public get operationMenuOpt(): IMenu[] {
		return [
			{
				label: this.trans.instant('BUTTON.EDIT'),
				icon: 'icon-edit',
				id: MenuItemAction.EDIT,
			},
			{
				label: this.trans.instant('BUTTON.DELETE'),
				icon: 'icon-trash',
				id: MenuItemAction.DELETE,
				divider: true,
				style: 'red',
			},
		];
	}

	public getDetailMenuOpt(detail: { status?: string }): IMenu[] {
		// Solo SuperAdmin puede editar/eliminar workflows
		if (!this.rolePermissions.canEditWorkflow()) return [];

		const canEdit = this.rolePermissions.canEditWorkflow();
		return buildCrudStatusMenu({
			activeValue: detail?.status === 'active',
			statusLabel: this.trans.instant('USERS.MENU.ACTIVE'),
			editLabel: this.trans.instant('BUTTON.EDIT'),
			deleteLabel: this.trans.instant('BUTTON.DELETE'),
			canToggleStatus: canEdit,
			canEdit,
			canDelete: canEdit,
		});
	}

	public get detailJsonMenuOpt(): IMenu[] {
		const options: IMenu[] = [];

		// Solo SuperAdmin puede editar el JSON del workflow
		if (this.rolePermissions.canEditWorkflow()) {
			options.push({
				label: this.trans.instant('BUTTON.EDIT'),
				icon: 'icon-edit',
				id: MenuItemAction.EDIT,
			});
		}

		// Descargar disponible para todos
		options.push({
			label: this.trans.instant('BUTTON.DOWNLOAD'),
			icon: 'icon-download',
			id: MenuItemAction.DOWNLOAD,
		});

		return options;
	}

	public get detailItemJsonMenuOpt(): IMenu[] {
		if (!this.rolePermissions.isSuperadmin()) return [];

		return [
			{
				label: this.trans.instant('BUTTON.SEE'),
				icon: 'icon-eye',
				id: MenuItemAction.SHOW,
			},
			{
				label: this.trans.instant('BUTTON.EDIT'),
				icon: 'icon-edit',
				id: MenuItemAction.EDIT,
			},
			{
				label: this.trans.instant('BUTTON.DOWNLOAD'),
				icon: 'icon-download',
				id: MenuItemAction.DOWNLOAD,
			},
		];
	}

	public get detailTabs(): ITab[] {
		return [
			{
				id: TAB_WORKFLOW_DETAIL.DETAILS,
				label: this.trans.instant('WORKFLOWS.DETAIL.TABS.DETAILS'),
				selected: true,
			},
			{ id: TAB_WORKFLOW_DETAIL.OPERATIONS, label: this.trans.instant('WORKFLOWS.DETAIL.TABS.OPERATIONS') },
		];
	}

	public get modalFraudScoringTabs(): ITab[] {
		return [
			{
				id: TAB_WORKFLOW_OCR_FRAUD.PHOTOCOPY,
				label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.PHOTOCOPY'),
				selected: true,
			},
			{ id: TAB_WORKFLOW_OCR_FRAUD.SCREENPHOTO, label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.PHOTO') },
			{
				id: TAB_WORKFLOW_OCR_FRAUD.MANIPULATED_PHOTO,
				label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.MANIPULADTED_IMAGE'),
			},
			{
				id: TAB_WORKFLOW_OCR_FRAUD.IDENTIFIER_BLOCKED,
				label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.IDENTIFIER_BLOCKED'),
			},
		];
	}

	public get modalDocDetailsTabs(): ITab[] {
		return [
			{
				id: TAB_WORKFLOW_DOC_DETAILS.CONFIGURATION,
				label: this.trans.instant('WORKFLOWS.DETAIL.TABS.CONFIGURATION'),
				selected: true,
			},
			{
				id: TAB_WORKFLOW_DOC_DETAILS.INSTRUCTIONS,
				label: this.trans.instant('WORKFLOWS.DETAIL.TABS.INSTRUCTIONS'),
			},
		];
	}

	public get form(): FormGroup {
		return this.formBuilder.form;
	}

	public get formOcrFraudScoring(): FormGroup {
		return this.formBuilder.formOcrFraudScoring;
	}

	public buildFinishResultMessagesFormGroup(): FormGroup {
		return this.formBuilder.buildFinishResultMessagesFormGroup();
	}

	public get formDoc(): FormGroup {
		return this.formBuilder.formDoc;
	}

	public get formFormField(): FormGroup {
		return this.formBuilder.formFormField;
	}

	public get formFormFieldOption(): FormGroup {
		return this.formBuilder.formFormFieldOption;
	}

	/** Fila de opción FORM: `label` es FormGroup por idioma (obligatorio en todos los locales). */
	public createFormFieldOptionGroup(richText = false): FormGroup {
		return this.formBuilder.createFormFieldOptionGroup(richText);
	}

	public get formFormStep(): FormGroup {
		return this.formBuilder.formFormStep;
	}

	public createFormFieldGroup(): FormGroup {
		return this.formBuilder.createFormFieldGroup();
	}

	public createFormStepGroup(): FormGroup {
		return this.formBuilder.createFormStepGroup();
	}

	/** Indica si el campo debe validar contra OCR (validators[] o control UI match_ocr). */
	public resolveMatchOcrFlag(field: Record<string, unknown>): boolean {
		return this.formBuilder.resolveMatchOcrFlag(field);
	}

	/** Valores para patchValue del FormGroup de campo (separa match_ocr de validators[]). */
	public patchFormFieldValue(field: Record<string, unknown>): Record<string, unknown> {
		return this.formBuilder.patchFormFieldValue(field);
	}

	/** Serializa un campo FORM para API: uuid, title i18n, match_ocr en validators[]. */
	public serializeFormField(field: Record<string, unknown>): Record<string, unknown> {
		return this.formBuilder.serializeFormField(field);
	}

	private formStepsMenuOptCache: IMenu[] | null = null;

	public get formStepsMenuOpt(): IMenu[] {
		if (!this.formStepsMenuOptCache) {
			this.formStepsMenuOptCache = [
				{
					id: MenuItemAction.SHOW,
					label: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.VIEW_STEP'),
					icon: 'icon-eye',
				},
				{
					id: MenuItemAction.EDIT,
					label: this.trans.instant('BUTTON.EDIT'),
					icon: 'icon-edit',
				},
				{
					id: MenuItemAction.DELETE,
					label: this.trans.instant('BUTTON.DELETE'),
					icon: 'icon-trash',
					divider: true,
					style: 'red',
				},
			];
		}
		return this.formStepsMenuOptCache;
	}

	public get docsInfo(): IDocInfo[] {
		// Define el orden deseado de los ids. Ajusta este array según el orden requerido.
		const ORDERED_IDS: string[] = [
			DOCS_TYPE.DRIVE_LICENSE,
			DOCS_TYPE.DNI,
			DOCS_TYPE.PASSPORT,
			DOCS_TYPE.NIE,
			DOCS_TYPE.CERT_REG_CIU_UE,
			DOCS_TYPE.IRPF,
			DOCS_TYPE.CHECK,
			DOCS_TYPE.INVOICE,
			DOCS_TYPE.WORKLIFE,
			DOCS_TYPE.PAYROLL,
			DOCS_TYPE.SCRIPTURE,
			DOCS_TYPE.GENERIC,
		];

		const orderIndex = (type: string) => {
			const idx = ORDERED_IDS.indexOf(type);
			return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
		};

		return this.masterService.documentTypes
			.map((doc: ISelect) => ({
				title: this.getExtraInfo(doc.id)?.title,
				description: this.getExtraInfo(doc.id)?.description,
				type: doc.id,
				icon: this.getExtraInfo(doc.id)?.icon,
				category: this.getExtraInfo(doc.id)?.category,
				uuid: doc.value,
				confRecommended: this.getExtraInfo(doc.id)?.confRecommended,
			}))
			.sort((a, b) => {
				const ia = orderIndex(a.type as string);
				const ib = orderIndex(b.type as string);
				if (ia !== ib) return ia - ib;
				return (a.title || '').localeCompare(b.title || '');
			});
	}

	private getExtraInfo(type: any): {
		title: string;
		description: string;
		icon: Icons;
		category: 'generic' | 'company' | 'personal';
		confRecommended: IDocInfo['confRecommended'];
	} {
		const docs = {
			[DOCS_TYPE.DRIVE_LICENSE]: () => ({
				title: this.trans.instant('DOCS.DRIVE_LICENSE'),
				description: this.trans.instant('DOCS.DRIVE_LICENSE_DESCRIPTION'),
				icon: 'icon-card',
				category: 'personal',
				confRecommended: {
					total_tries: 3,
					min: 1,
					max: 2,
					preview_validation: true,
					match_data: true,
					connection_available: false,
				},
			}),
			[DOCS_TYPE.DNI]: () => ({
				title: this.trans.instant('DOCS.DNI'),
				description: this.trans.instant('DOCS.DNI_DESCRIPTION'),
				icon: 'icon-card',
				category: 'personal',
				confRecommended: {
					total_tries: 3,
					min: 2,
					max: 2,
					preview_validation: true,
					match_data: true,
					connection_available: false,
				},
			}),
			[DOCS_TYPE.PASSPORT]: () => ({
				title: this.trans.instant('DOCS.PASSPORT'),
				description: this.trans.instant('DOCS.PASSPORT_DESCRIPTION'),
				icon: 'icon-card',
				category: 'personal',
				confRecommended: {
					total_tries: 3,
					min: 1,
					max: 2,
					preview_validation: true,
					match_data: true,
					connection_available: false,
				},
			}),
			[DOCS_TYPE.NIE]: () => ({
				title: this.trans.instant('DOCS.NIE'),
				description: this.trans.instant('DOCS.NIE_DESCRIPTION'),
				icon: 'icon-card',
				category: 'personal',
				confRecommended: {
					total_tries: 3,
					min: 2,
					max: 2,
					preview_validation: true,
					match_data: true,
					connection_available: false,
				},
			}),
			[DOCS_TYPE.CERT_REG_CIU_UE]: () => ({
				title: this.trans.instant('DOCS.CERT_REG_CIU_UE'),
				description: this.trans.instant('DOCS.CERT_REG_CIU_UE_DESCRIPTION'),
				icon: 'icon-card',
				category: 'personal',
				confRecommended: {
					total_tries: 3,
					min: 1,
					max: 10,
					preview_validation: true,
					match_data: true,
					connection_available: false,
				},
			}),
			[DOCS_TYPE.IRPF]: () => ({
				title: this.trans.instant('DOCS.IRPF'),
				description: this.trans.instant('DOCS.IRPF_DESCRIPTION'),
				icon: 'icon-euro',
				category: 'personal',
				confRecommended: {
					total_tries: 3,
					min: 1,
					max: 10,
					preview_validation: true,
					match_data: true,
					connection_available: true,
					connection_uuid: this.masterService.connectionTypes.find(
						(connection: ISelect) => connection.id === CONNECTION_TYPE.IRPF,
					)?.value,
					connection_type: CONNECTION_TYPE.IRPF,
				},
			}),
			[DOCS_TYPE.CHECK]: () => ({
				title: this.trans.instant('DOCS.CHECK'),
				description: this.trans.instant('DOCS.CHECK_DESCRIPTION'),
				icon: 'icon-euro',
				category: 'personal',
				confRecommended: {
					total_tries: 3,
					min: 1,
					max: 1,
					preview_validation: true,
					match_data: true,
					connection_available: false,
				},
			}),
			[DOCS_TYPE.INVOICE]: () => ({
				title: this.trans.instant('DOCS.INVOICE'),
				description: this.trans.instant('DOCS.INVOICE_DESCRIPTION'),
				icon: 'icon-euro',
				category: 'company',
				confRecommended: {
					total_tries: 3,
					min: 1,
					max: 10,
					preview_validation: true,
					match_data: true,
					connection_available: false,
				},
			}),
			[DOCS_TYPE.WORKLIFE]: () => ({
				title: this.trans.instant('DOCS.WORKLIFE'),
				description: this.trans.instant('DOCS.WORKLIFE_DESCRIPTION'),
				icon: 'icon-briefcase',
				category: 'personal',
				confRecommended: {
					total_tries: 3,
					min: 1,
					max: 1,
					preview_validation: true,
					match_data: true,
					connection_available: true,
					connection_uuid: this.masterService.connectionTypes.find(
						(connection: ISelect) => connection.id === CONNECTION_TYPE.WORK_LIFE,
					)?.value,
					connection_type: CONNECTION_TYPE.WORK_LIFE,
				},
			}),
			[DOCS_TYPE.PAYROLL]: () => ({
				title: this.trans.instant('DOCS.PAYROLL'),
				description: this.trans.instant('DOCS.PAYROLL_DESCRIPTION'),
				icon: 'icon-briefcase',
				category: 'personal',
				confRecommended: {
					total_tries: 3,
					min: 1,
					max: 10,
					preview_validation: true,
					match_data: true,
					connection_available: false,
				},
			}),
			[DOCS_TYPE.SCRIPTURE]: () => ({
				title: this.trans.instant('DOCS.SCRIPTURE'),
				description: this.trans.instant('DOCS.SCRIPTURE_DESCRIPTION'),
				icon: 'icon-briefcase',
				category: 'company',
				confRecommended: {
					total_tries: 3,
					min: 1,
					max: 10,
					preview_validation: true,
					match_data: true,
					connection_available: true,
					connection_uuid: this.masterService.connectionTypes.find(
						(connection: ISelect) => connection.id === CONNECTION_TYPE.SCRIPTURE,
					)?.value,
					connection_type: CONNECTION_TYPE.SCRIPTURE,
				},
			}),
			[DOCS_TYPE.GENERIC]: () => ({
				title: this.trans.instant('DOCS.GENERIC'),
				description: this.trans.instant('DOCS.GENERIC_DESCRIPTION'),
				icon: 'icon-book-open',
				category: 'generic',
				confRecommended: {
					total_tries: 3,
					min: 1,
					max: 10,
					preview_validation: false,
					match_data: false,
					connection_available: false,
				},
			}),
		};

		return docs[type] ? docs[type]() : null;
	}

	public get demandLevelsPol(): IPolDemandLevel[] {
		return [
			{
				id: DEMAND_LEVEL.STANDARD,
				total_test: 3,
				total_tries: 2,
				type: POL_TYPE.RANDOM,
				scoring: CONFIDENCE_THRESHOLD.MAX,
				max_time: 300,
				available_tests: [
					POL_TEST.MIDDLE_FACE,
					POL_TEST.SMILE,
					POL_TEST.TURN_RIGHT_HEAD,
					POL_TEST.TURN_LEFT_HEAD,
				],
			},
			{
				id: DEMAND_LEVEL.MAX_FLUID,
				total_test: 2,
				total_tries: 2,
				type: POL_TYPE.RANDOM,
				scoring: CONFIDENCE_THRESHOLD.STANDARD,
				max_time: 250,
				available_tests: [POL_TEST.MIDDLE_FACE, POL_TEST.SMILE],
			},
		];
	}

	public sanitizePolAvailableTests(tests: POL_TEST[] | undefined): POL_TEST[] {
		return sanitizePolAvailableTestsUtil(tests);
	}

	public sanitizePolTotalTest(totalTest: number | undefined): number {
		return sanitizePolTotalTestUtil(totalTest);
	}

	public sanitizePolSetting(setting: IPolSetting | undefined): IPolSetting {
		return sanitizePolSettingUtil(setting);
	}

	public selectPolDemandLevel(setting: IPolSetting): DEMAND_LEVEL {
		return selectPolDemandLevelUtil(setting, this.demandLevelsPol);
	}

	public getScoring(setting: IPolSetting): string {
		return getPolScoringLabel(setting);
	}

	public get operations(): IOperationInfo[] {
		return [
			{
				type: OPERATION_TYPE.HOME,
				disabled: false,
				show: this.form.get(`operations.${OPERATION_TYPE.HOME}.available`).value,
				selected: true,
				icon: 'icon-home',
				title: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.HOME'),
				description: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.HOME_DESCRIPTION'),
				image: 'images/workflow-home-unselect.png',
			},
			{
				type: OPERATION_TYPE.FORM,
				disabled: true,
				show: this.form.get(`operations.${OPERATION_TYPE.FORM}.available`).value,
				selected: false,
				icon: 'icon-list-ticks' as Icons,
				title: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.FORM'),
				description: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.FORM_TILE_DESCRIPTION'),
				image: 'images/workflow-form-unselect.png',
			},
			{
				type: OPERATION_TYPE.OCR,
				disabled: true,
				show: this.form.get(`operations.${OPERATION_TYPE.OCR}.available`).value,
				selected: false,
				icon: 'icon-card',
				title: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.OCR'),
				description: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.OCR_DESCRIPTION'),
				image: 'images/workflow-ocr-unselect.png',
			},
			{
				type: OPERATION_TYPE.POL,
				disabled: true,
				show: this.form.get(`operations.${OPERATION_TYPE.POL}.available`).value,
				selected: false,
				icon: 'icon-user',
				title: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.POL'),
				description: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.POL_DESCRIPTION'),
				image: 'images/workflow-pol-unselect.png',
			},
			{
				type: OPERATION_TYPE.ATTORNEY_POWERS,
				disabled: true,
				show: this.form.get(`operations.${OPERATION_TYPE.ATTORNEY_POWERS}.available`).value,
				selected: false,
				icon: 'icon-shield-check',
				title: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.ATTORNEY_POWERS'),
				description: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.ATTORNEY_POWERS_DESCRIPTION'),
				image: 'images/workflow-attorney-powers-unselect.png',
			},
			{
				type: OPERATION_TYPE.DOCUMENTS,
				disabled: true,
				show: this.form.get(`operations.${OPERATION_TYPE.DOCUMENTS}.available`).value,
				selected: false,
				icon: 'icon-file',
				title: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.DOCUMENTS'),
				description: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.DOCUMENTS_DESCRIPTION'),
				image: 'images/workflow-docs-unselect.png',
			},
			{
				type: OPERATION_TYPE.CONNECTIONS,
				disabled: true,
				show: this.form.get(`operations.${OPERATION_TYPE.CONNECTIONS}.available`).value,
				selected: false,
				icon: 'icon-euro',
				title: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.CONNECTIONS'),
				description: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.CONNECTIONS_DESCRIPTION'),
				image: 'images/workflow-eval-unselect.png',
			},
			{
				type: OPERATION_TYPE.FINISH,
				disabled: true,
				show: true,
				selected: false,
				icon: 'icon-external-link',
				title: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.FINISH'),
				description: this.trans.instant('WORKFLOWS.FORM.OPERATIONS.FINISH_DESCRIPTION'),
				image: 'images/workflow-finish.png',
			},
		];
	}

	public truncateUUID(uuid: string): string {
		return truncateUUID(uuid);
	}

	public get tecalisPolicy(): string {
		return this.trans.instant('WORKFLOWS.FORM.OPERATIONS.INSTR_CONDITIONS', {
			termsHref: PDFS.TERMS.path,
			privacyHref: PDFS.PRIVACY.path,
		});
	}
}
